import { FetchDataOverviewByDataTypeState } from './DataTypeUpdatesOverview';
import { DATA_TYPES, getDataType } from '@constants';
import styled from 'styled-components';
import { Doughnut } from 'react-chartjs-2';
import { conditionallyRenderElement } from 'utils';
import { useTheme } from '@mui/material';

const getOptions = (textColor: string) => ({
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top' as const,
      labels: {
        color: textColor, // Theme-aware text color for light/dark mode
        font: {
          size: 12,
          weight: 500,
        },
        padding: 15,
        usePointStyle: true,
        pointStyle: 'rectRounded' as const,
        boxWidth: 12,
        boxHeight: 12,
      },
    },
  },
});

type AccumulatorType = {
  labels: string[];
  counts: number[];
  backgroundColor: string[];
};

const DataTypeUpdatesOverviewChart = ({
  dataOverviewByDataTypeHash,
  isLoading,
}: {
  dataOverviewByDataTypeHash: FetchDataOverviewByDataTypeState['dataOverviewByDataTypeHash'];
  isLoading: boolean;
}) => {
  const theme = useTheme();
  const options = getOptions(theme.palette.text.primary);

  const getChartData = () => {
    const newChartData: {
      labels: AccumulatorType['labels'];
      datasets: {
        data: AccumulatorType['counts'];
        backgroundColor: AccumulatorType['backgroundColor'];
        borderWidth: number;
      }[];
    } = {
      labels: [],
      datasets: [],
    };

    if (dataOverviewByDataTypeHash) {
      const { labels, counts, backgroundColor } = DATA_TYPES.reduce(
        (acc: AccumulatorType, dataType) => {
          const dataOverview = dataOverviewByDataTypeHash[dataType];
          const dataTypeConst = getDataType(dataType);

          if (dataOverview && dataTypeConst) {
            const { label, color } = dataTypeConst;
            // Safely parse count value, default to 0 if invalid
            const count = parseInt(dataOverview.countofdatatype, 10);
            const validCount = !isNaN(count) && isFinite(count) ? count : 0;

            acc.labels.push(label);
            acc.counts.push(validCount);
            acc.backgroundColor.push(color);
          }
          return acc;
        },
        {
          labels: [],
          counts: [],
          backgroundColor: [],
        },
      );

      newChartData.labels = labels;
      newChartData.datasets = [
        {
          data: counts,
          backgroundColor: backgroundColor,
          borderWidth: 2,
        },
      ];
    }

    return newChartData;
  };

  return conditionallyRenderElement({
    isLoading,
    error: undefined,
    content: (
      <ChartWrapper>
        <DoughnutInner />
        <Doughnut data={getChartData()} options={options} />
      </ChartWrapper>
    ),
  });
};

export default DataTypeUpdatesOverviewChart;

const DoughnutInner = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 0;
  margin-top: -22px;
  text-align: center;
  z-index: 0;
`;

const ChartWrapper = styled.div`
  position: relative;
`;
